import React from 'react';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Database } from '../types/supabase';
import { useSearchParams } from 'react-router-dom';
import TaskList from '../components/TaskList';
import TaskForm from '../components/TaskForm';
import { Plus, LayoutList, LayoutGrid, Copy } from 'lucide-react';
import Navigation from '../components/Navigation';
import KanbanView from '../components/KanbanView';
import { toast } from '../lib/toast';
import { User } from '@supabase/supabase-js';

type Task = Database['public']['Tables']['tasks']['Row'];
type Agency = Database['public']['Tables']['agencies']['Row'];
type Project = Database['public']['Tables']['projects']['Row'];

export default function Tasks({ navigate }: { navigate: (to: string) => void }) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(searchParams.get('showForm') === 'true');
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [currentView, setCurrentView] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [selectedAgencyId, setSelectedAgencyId] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const handleDuplicate = async (task: Task) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to duplicate tasks');
        return;
      }

      const newTask = {
        title: `${task.title} (Copy)`,
        description: task.description,
        priority: task.priority,
        project_id: task.project_id,
        agency_id: task.agency_id,
        due_date: task.due_date,
        status: task.status || 'Not Started',
        completed: false,
        user_id: user.id
      };

      const { data, error } = await supabase
        .from('tasks')
        .insert([newTask])
        .select()
        .single();

      if (error) throw error;

      toast.success('Task duplicated successfully');
      await fetchTasks();
    } catch (error) {
      console.error('Error duplicating task:', error);
      toast.error('Failed to duplicate task');
    }
  };

  useEffect(() => {
    const getSession = async () => {
      try {
        setFetchError(null);
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          fetchTasks();
          fetchAgenciesAndProjects();
        }
      } catch (error) {
        console.error('Auth error:', error);
        setFetchError('Authentication error. Please try refreshing the page.');
      }
    };
    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        fetchTasks();
        fetchAgenciesAndProjects();
      } else {
        setTasks([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchAgenciesAndProjects = async () => {
    try {
      setFetchError(null);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setAgencies([]);
        setProjects([]);
        setFilteredProjects([]);
        return;
      }
      
      const { data: agenciesData, error: agenciesError } = await supabase
        .from('agencies')
        .select('id, name, contact_first_name, contact_last_name, email, phone, created_at, logo_url, additional_info')
        .eq('user_id', session.user.id)
        .order('name');

      if (agenciesError) {
        console.error('Error fetching agencies:', agenciesError);
        throw agenciesError;
      }
      
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select('id, name, agency_name, priority, status, description, created_at')
        .eq('user_id', session.user.id)
        .order('name');

      if (projectsError) {
        console.error('Error fetching projects:', projectsError);
        throw projectsError;
      }

      setAgencies(agenciesData || []);
      setProjects(projectsData || []);
      setFilteredProjects(projectsData || []);
    } catch (error) {
      console.error('Error fetching filters data:', error);
      setFetchError('Failed to load filter data. Please try again.');
    }
  };

  useEffect(() => {
    if (selectedAgencyId) {
      const selectedAgency = agencies.find(a => a.id === selectedAgencyId);
      setFilteredProjects(
        projects.filter(project => project.agency_name === selectedAgency?.name)
      );
      setSelectedProjectId('');
    } else {
      setFilteredProjects(projects);
    }
  }, [selectedAgencyId, projects, agencies]);

  const handleUpdateStatus = async (taskId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ status })
        .eq('id', taskId);

      if (error) throw error;
      setTasks(
        tasks.map((task) =>
          task.id === taskId ? { ...task, status } : task
        )
      );
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status');
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setTasks([]);
        return;
      }
      
      const { data, error } = await supabase 
        .from('tasks')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTasks(data || []);
      
      // Fetch agencies and projects after tasks are loaded
      await fetchAgenciesAndProjects();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (taskId: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;
      setTasks(tasks.filter((task) => task.id !== taskId));
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleToggleComplete = async (taskId: string, completed: boolean) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ completed })
        .eq('id', taskId);

      if (error) throw error;
      setTasks(
        tasks.map((task) =>
          task.id === taskId ? { ...task, completed } : task
        )
      );
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleUpdatePriority = async (taskId: string, priority: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ priority })
        .eq('id', taskId);

      if (error) throw error;
      setTasks(
        tasks.map((task) =>
          task.id === taskId ? { ...task, priority } : task
        )
      );
      toast.success(`Task priority updated to ${priority}`);
    } catch (error) {
      console.error('Error updating task priority:', error);
      toast.error('Failed to update task priority');
    }
  };

  const filteredTasks = tasks.filter((task) => {
    if (selectedAgencyId && task.agency_id !== selectedAgencyId) return false;
    if (selectedProjectId && task.project_id !== selectedProjectId) return false;

    switch (currentView) {
      case 'due-soon':
        return !task.completed && 
               task.due_date && 
               new Date(task.due_date) <= new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
      case 'low':
        return task.priority === 'Low';
      case 'normal':
        return task.priority === 'Normal';
      case 'high':
        return task.priority === 'High';
      case 'urgent':
        return task.priority === 'Urgent';
      default:
        return true;
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={tasks}
        currentView={currentView}
        onViewChange={setCurrentView}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="flex flex-col space-y-6">
            {/* Top Controls */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => {
                    setEditingTask(null);
                    setShowForm(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add Task
                </button>
                <div className="flex items-center space-x-2 border rounded-md">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${
                      viewMode === 'list'
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <LayoutList className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('kanban')}
                    className={`p-2 ${
                      viewMode === 'kanban'
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <LayoutGrid className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Priority Filters */}
              <div className="flex space-x-2">
                {['all', 'due-soon', 'low', 'normal', 'high', 'urgent'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setCurrentView(filter)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      currentView === filter
                        ? filter === 'due-soon'
                          ? 'bg-blue-100 text-blue-700'
                          : filter === 'low'
                          ? 'bg-gray-100 text-gray-700'
                          : filter === 'normal'
                          ? 'bg-green-100 text-green-700'
                          : filter === 'high'
                          ? 'bg-orange-100 text-orange-700'
                          : filter === 'urgent'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <span className={
                      filter === 'due-soon'
                        ? 'text-blue-700'
                        : filter === 'low'
                        ? 'text-gray-700'
                        : filter === 'normal'
                        ? 'text-green-700'
                        : filter === 'high'
                        ? 'text-orange-700'
                        : filter === 'urgent'
                        ? 'text-red-700'
                        : ''
                    }>
                      {filter === 'due-soon' ? 'Due Soon' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Customer and Project Filters */}
            <div className="flex flex-wrap gap-4 items-center sm:justify-end">
              <div className="w-64">
                <label htmlFor="customer-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Filter by Customer
                </label>
                <select
                  id="customer-filter"
                  value={selectedAgencyId}
                  onChange={(e) => setSelectedAgencyId(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Customers</option>
                  {agencies.map((agency) => (
                    <option key={agency.id} value={agency.id}>
                      {agency.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-64">
                <label htmlFor="project-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Filter by Project
                </label>
                <select
                  id="project-filter"
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Projects</option>
                  {filteredProjects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Main Content */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
              </div>
            ) : fetchError ? (
              <div className="text-center py-12">
                <p className="text-red-600 dark:text-red-400 mb-4">{fetchError}</p>
                <button
                  onClick={() => {
                    setFetchError(null);
                    fetchTasks();
                    fetchAgenciesAndProjects();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Retry
                </button>
              </div>
            ) : showForm ? (
              <div className="bg-white shadow sm:rounded-lg p-6">
                <TaskForm
                  onSuccess={() => {
                    setShowForm(false);
                    fetchTasks();
                  }}
                  onCancel={() => {
                    setShowForm(false);
                    setEditingTask(null);
                  }}
                  initialData={editingTask || undefined}
                />
              </div>
            ) : viewMode === 'list' ? (
              <TaskList
                tasks={filteredTasks}
                navigate={navigate}
                onEdit={(task) => {
                  setEditingTask(task);
                  setShowForm(true);
                }}
                onDelete={handleDelete}
                onToggleComplete={handleToggleComplete}
                onUpdateStatus={handleUpdateStatus}
                onUpdatePriority={handleUpdatePriority}
                onDuplicate={handleDuplicate}
              />
            ) : (
              <KanbanView
                tasks={filteredTasks}
                onEdit={(task) => {
                  setEditingTask(task);
                  setShowForm(true);
                }}
                onDelete={handleDelete}
                onToggleComplete={handleToggleComplete}
                onUpdatePriority={handleUpdatePriority}
                onUpdateStatus={handleUpdateStatus}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}